import { useMountEffect } from '@timelyapp/react';
import classNames from 'classnames';
import DownloadIntegrationButton from 'components/buttons/DownloadIntegrationButton';
import { Checkmark, MemoryApp } from 'components/Icons';
import Spinner from 'components/Spinner';
import type { HTMLMotionProps } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { fetchApps } from 'redux/modules/apps.actions';
import { getShouldGetNewMemoryVersion } from 'redux/selectors/accounts';
import { getAppsForAppsList } from 'redux/selectors/apps';
import { TrackingActions } from 'tracking/actions';
import type { DownloadMemorySource } from 'tracking/constants';
import type { MemoryTracker } from 'types/memoryTracker';
import type { ThunkDispatch } from 'types/redux';
import { isAppConnected } from 'utils/apps';
import styles from './DownloadMemoryButton.module.scss';

interface DownloadMemoryButtonProps
  extends Omit<React.HTMLProps<HTMLButtonElement>, 'action' | 'ref'> {
  onConnected?: () => void;
  onDownload?: () => void;
  tracker: MemoryTracker;
  source: DownloadMemorySource;

  // These properties help with Storybook instrumentation
  hasConnectedMemory?: boolean;
  isInstallingMemory?: boolean;
}

const installationProgressProps: HTMLMotionProps<'div'> = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const DownloadMemoryButton: React.FC<DownloadMemoryButtonProps> = ({
  className,
  isInstallingMemory: isInstallingMemoryFromProps,
  hasConnectedMemory: hasConnectedMemoryFromProps,
  onClick,
  onConnected,
  onDownload,
  tracker,
  source,
  ...props
}) => {
  const [isInstallingMemory, setIsInstallingMemory] = useState(
    isInstallingMemoryFromProps ?? false
  );

  const initialApps = useSelector(getAppsForAppsList);
  const initialHasConnectedMemory = useMemo(
    () => isAppConnected(tracker.id, initialApps.connectedApps),
    [initialApps.connectedApps, tracker]
  );

  useMountEffect(() => {
    if (initialHasConnectedMemory) {
      onConnected?.();
    }
  });

  const [hasConnectedMemory, setHasConnectedMemory] = useState(
    hasConnectedMemoryFromProps ?? initialHasConnectedMemory
  );
  const dispatch = useDispatch<ThunkDispatch>();

  const trackDownload = useCallback(() => {
    TrackingActions.downloadedMemory(source, tracker.id);
  }, [source, tracker]);

  const pollInterval = useRef<number | undefined>();
  useEffect(() => {
    pollInterval.current = window.setInterval(async function pollForMemory() {
      const apps = await dispatch(fetchApps());
      if (isAppConnected(tracker.id, apps)) {
        batch(() => {
          setIsInstallingMemory(false);
          setHasConnectedMemory(true);
          onConnected?.();
        });
        window.clearInterval(pollInterval.current);
      }
    }, 3000);

    return () => {
      window.clearInterval(pollInterval.current);
    };
  }, [dispatch, isInstallingMemory, onConnected, tracker]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!isInstallingMemory) {
        setIsInstallingMemory(true);
      }
    },
    [isInstallingMemory, onClick]
  );

  const getNewVersion = useSelector(getShouldGetNewMemoryVersion);
  const downloadLink =
    typeof tracker.downloadLink === 'function'
      ? tracker.downloadLink(getNewVersion)
      : tracker.downloadLink;

  return (
    <div className={styles.container}>
      <AnimatePresence>
        {!hasConnectedMemory && isInstallingMemory && (
          <motion.div
            key="waiting"
            className={classNames(styles.installationProgress, styles.waiting)}
            {...installationProgressProps}
          >
            <Spinner className={styles.spinner} /> &nbsp; Waiting for you to install {tracker.name}
          </motion.div>
        )}
        {hasConnectedMemory && (
          <motion.div
            key="success"
            className={classNames(styles.installationProgress, styles.complete)}
            {...installationProgressProps}
          >
            <Checkmark /> &nbsp; Installation successful
          </motion.div>
        )}
      </AnimatePresence>
      <DownloadIntegrationButton
        icon={MemoryApp}
        heading={tracker.name}
        description={tracker.description}
        action={tracker.action}
        downloadLink={downloadLink}
        openInNewWindow={tracker.downloadInNewWindow}
        onClick={handleClick}
        onDownload={trackDownload}
        className={classNames(className, styles.button, {
          [styles.fadedOut]: hasConnectedMemory || isInstallingMemory,
        })}
        {...props}
      />
    </div>
  );
};

export default DownloadMemoryButton;
